/**
 * #427 W1a — /timeclock: the punch surface for desk roles whose home
 * isn't a persona queue (spec §5 W1a). Hosts the Time Clock card plus a
 * tucked-away "Set my kiosk PIN" disclosure (spec §4.1 PUT /me/kiosk-pin).
 *
 * Design Philosophy principles served:
 *   P2 (one primary per view) — the card's punch button is the page's
 *     sole filled-primary (`primaryDemoted` stays false: this page has
 *     no competing queue); the PIN affordance is a ghost disclosure,
 *     never a second primary.
 *   P7 (built for hands) — ≥44px PIN input/buttons, numeric keyboard.
 */
import { useState } from 'react';
import type { ClientConfig } from '@ferro/config';
import { PageHeader } from '@ferro/ui';
import { useAuth } from '../contexts/AuthContext.js';
import { adminTimeclockApi, humanizeApiError } from '../lib/api.js';
import { TimeClockCard } from '../components/timeclock/TimeClockCard.js';

const PIN_PATTERN = /^\d{6}$/;

export function Timeclock({ config }: { config: ClientConfig }): JSX.Element {
  const { staff } = useAuth();
  const apiUrl = config.deployment.apiUrl;

  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinResult, setPinResult] = useState<{ kind: 'success' | 'error'; message: string } | null>(
    null,
  );

  const handleSavePin = async (): Promise<void> => {
    if (!PIN_PATTERN.test(pin)) {
      setPinResult({ kind: 'error', message: 'A kiosk PIN is exactly 6 digits — numbers only.' });
      return;
    }
    setPinSaving(true);
    setPinResult(null);
    const res = await adminTimeclockApi.setMyPin(apiUrl, pin);
    setPinSaving(false);
    if (res.error !== undefined) {
      setPinResult({ kind: 'error', message: humanizeApiError(res.error) });
      return;
    }
    setPin('');
    setPinResult({
      kind: 'success',
      message: 'PIN saved — you can now punch in on the facility tablet.',
    });
  };

  return (
    <section className="ferro-admin-page ferro-timeclock-page">
      <PageHeader
        title={`Time Clock${staff?.first_name ? ` · ${staff.first_name}` : ''}`}
        subtitle="Punch in and out — your hours count from each tap"
      />

      <TimeClockCard config={config} />

      {/* Secondary disclosure — quiet by design so the punch button
          stays the page's one primary (P2). */}
      <div className="ferro-timeclock-pin" data-testid="timeclock-pin-section">
        {!pinOpen ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            data-testid="timeclock-pin-toggle"
            onClick={() => setPinOpen(true)}
          >
            Set my kiosk PIN
          </button>
        ) : (
          <>
            <p className="ferro-timeclock-pin__hint">
              Your PIN lets you punch in on the facility tablet — tap your name
              there, then enter these 6 digits. Saving replaces any previous PIN.
            </p>
            <div className="ferro-timeclock-pin__form">
              <input
                className="ferro-timeclock-pin__input"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                placeholder="••••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                aria-label="New 6-digit kiosk PIN"
                data-testid="timeclock-pin-input"
              />
              <button
                type="button"
                className="ferro-btn ferro-btn--outline"
                data-testid="timeclock-pin-save"
                disabled={pinSaving || pin.length !== 6}
                aria-busy={pinSaving}
                onClick={() => void handleSavePin()}
              >
                {pinSaving ? 'Saving…' : 'Save PIN'}
              </button>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => {
                  setPinOpen(false);
                  setPin('');
                  setPinResult(null);
                }}
              >
                Cancel
              </button>
            </div>
            {pinResult && (
              <div
                className={`ferro-alert ferro-alert--${pinResult.kind}`}
                role={pinResult.kind === 'error' ? 'alert' : 'status'}
                data-testid="timeclock-pin-result"
              >
                {pinResult.message}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
export default Timeclock;
